/**
 * One saved rule in the automations list. A component rather than markup inside
 * the list's `map` because the meta line names the rule's author, and turning a
 * user id into a name is a hook.
 */
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  CopyIcon,
  EllipsisIcon,
  HistoryIcon,
  PencilIcon,
  PlayIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from 'lucide-react';

import type { TrackerAutomation } from '@/entities/tracker-automation';
import type { BrokenReference } from '../../lib/automation-model';
import { useUserDisplay } from '@/entities/user';
import { DragHandle } from '@/shared/ui';
import { formatTimeAgo } from '@/shared/utils/dateUtils';

import { dateTimeText } from '../../lib/automation-runs';

interface AutomationRuleRowProps {
  rule: TrackerAutomation;
  /** 1-based place in the order the rules apply in. */
  order: number;
  /** The rule as a sentence, already resolved against the board's columns. */
  summary: string;
  /** Absent for someone who may only read the rules: the row shows them, nothing more. */
  manage?: {
    onEnabledChange: (enabled: boolean) => void;
    onEdit: () => void;
    /** Absent while the board or organization holds all the rules it may. */
    onDuplicate?: () => void;
    onDelete: () => void;
  };
  /** Opens what the rule has done. Open to everyone who can see the rule. */
  onHistory: () => void;
  /** Runs the rule on the rows that already match it. Absent where it cannot run. */
  onRun?: () => void;
  /** What the rule points at that the board deleted, said in place of the run summary. */
  broken?: BrokenReference | null;
}

export function AutomationRuleRow({
  rule,
  order,
  summary,
  onHistory,
  manage,
  onRun,
  broken = null,
}: AutomationRuleRowProps) {
  const { t } = useTranslation('trackers');

  const author = useUserDisplay(rule.createdBy);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id,
    disabled: !manage,
  });
  const label = rule.name ?? summary;

  // A rule that has never fired still gets both leading segments, so the line
  // keeps its shape from the first render instead of growing as runs arrive.
  // The author is dropped rather than stubbed while the name is still loading.
  const meta = [
    rule.runCount > 0
      ? t('trackersBoard.automationsPanel.runsCount', { count: rule.runCount })
      : t('trackersBoard.automationsPanel.noRunsYet'),
    rule.lastRunAt
      ? t('trackersBoard.automationsPanel.lastRun', { timestamp: formatTimeAgo(rule.lastRunAt) })
      : t('trackersBoard.automationsPanel.lastNever'),
    author.name ? t('trackersBoard.automationsPanel.byAuthor', { author: author.name }) : null,
  ]
    .filter(Boolean)
    .join(t('trackersBoard.automationsPanel.metaSeparator'));

  const switchLabel = rule.enabled
    ? t('trackersBoard.automationsPanel.pauseAutomation')
    : t('trackersBoard.automationsPanel.activateAutomation');

  const moreActions = t('trackersBoard.automationsPanel.moreActions');

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={clsx(
        'relative flex items-start gap-3 rounded-lg border border-border bg-background px-3 pt-5 pb-3',
        manage && 'hover:bg-accent',
        isDragging && 'opacity-50',
      )}
    >
      <span className="absolute top-0 left-0 rounded-tl-lg rounded-br-md border-r border-b border-border bg-muted px-1.5 text-xs leading-4 text-muted-foreground tabular-nums">
        {order}
      </span>

      {manage ? (
        <DragHandle
          ariaLabel={t('trackersBoard.automationsPanel.reorderAutomationNamed', { summary: label })}
          // Its own `title` rather than a Tooltip: DragHandle takes no ref, so
          // Radix's asChild clone would be dropped.
          title={t('trackersBoard.automationsPanel.reorder')}
          attributes={attributes}
          listeners={listeners}
          className="relative z-10 mt-0.5"
        />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {manage ? (
          // Stretched over the whole row by its `after` box, so a click anywhere
          // on the row opens the rule while the controls above it (z-10) keep
          // their own clicks.
          <button
            type="button"
            onClick={manage.onEdit}
            aria-label={t('trackersBoard.automationsPanel.editAutomationNamed', { summary: label })}
            className={clsx(
              "text-left text-sm font-medium break-words after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring",
              rule.enabled ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {label}
          </button>
        ) : (
          <span
            className={clsx(
              'text-sm font-medium break-words',
              rule.enabled ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {label}
          </span>
        )}
        {rule.name ? <span className="text-xs text-muted-foreground">{summary}</span> : null}
        {broken ? (
          <span className="flex items-start gap-1 text-xs text-warning-foreground">
            <TriangleAlertIcon className="mt-px size-3.5 shrink-0" aria-hidden />
            {t(`trackersBoard.automationsPanel.brokenReference.${broken}`)}
          </span>
        ) : (
          // `formatTimeAgo` buckets to whole months past four weeks, so the
          // exact time is otherwise unreachable to someone working out when a
          // rule stopped firing. Raised above the row's click box, which would
          // otherwise swallow the hover that shows it.
          <span
            className="relative z-10 self-start text-xs text-muted-foreground"
            title={rule.lastRunAt ? dateTimeText(rule.lastRunAt) : undefined}
          >
            {meta}
          </span>
        )}
      </div>

      <div className="relative z-10 flex shrink-0 items-center gap-2">
        {/* Tessra's Switch is always primary when on, with no variant for
            another colour, so the green is set here. */}
        <Switch
          checked={rule.enabled}
          onCheckedChange={manage?.onEnabledChange}
          disabled={!manage}
          aria-label={switchLabel}
          title={switchLabel}
          className="data-[state=checked]:bg-positive-solid"
        />

        <TooltipProvider>
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-mini"
                    aria-label={t('trackersBoard.automationsPanel.moreActionsNamed', {
                      summary: label,
                    })}
                  >
                    <EllipsisIcon className="size-3.5" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{moreActions}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-44">
              {onRun ? (
                <DropdownMenuItem onSelect={onRun}>
                  <PlayIcon aria-hidden />
                  {t('trackersBoard.automationsPanel.runNow')}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onSelect={onHistory}>
                <HistoryIcon aria-hidden />
                {t('trackersBoard.automationRuns.history')}
              </DropdownMenuItem>
              {manage ? (
                <>
                  <DropdownMenuItem onSelect={manage.onEdit}>
                    <PencilIcon aria-hidden />
                    {t('trackersBoard.automationsPanel.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={manage.onDuplicate} disabled={!manage.onDuplicate}>
                    <CopyIcon aria-hidden />
                    {t('trackersBoard.automationsPanel.duplicate')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onSelect={manage.onDelete}>
                    <Trash2Icon aria-hidden />
                    {t('trackersBoard.automationsPanel.delete')}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </TooltipProvider>
      </div>
    </li>
  );
}
